import React, { useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'
import LoadingButton from '@mui/lab/LoadingButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import {
  Alert,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
} from '@mui/material'
import Link from 'next/link'
import { Mixpanel } from '@/utils/mixpanel'
import { useRouter } from 'next/router'
import {
  getOrderConfirmationFromService,
  submitUserContactToService,
} from '@/src/services/sipjoyService'
import { useSite } from '@/src/contexts/SiteContext';

export const CONTACT_FROM_USER = 'user'
export const QUIZ_FEEDBACK = 'quiz_feedback'

const CONTACT_REASON_ADDRESS_CHANGE = 'Change order address'
const CONTACT_REASON_ORDER_SUPPORT = 'Order support'
const CONTACT_REASON_GENERAL_INQUIRY = 'General inquiry'

const Form = ({ contactType = CONTACT_FROM_USER }) => {
  const { site } = useSite()
  const router = useRouter()
  const { query } = useRouter()

  const orderKey = query.order
  const theme = useTheme()
  const [contactDetails, setContactDetails] = useState({})
  const [dataSaveStatus, setDataSaveStatus] = useState({ loading: false })

  let showUserDetails = true
  let emailRequired = true
  let messageLabel = 'Type your inquiry or updated address here'
  let header
  let successMsg =
    "Your message was sent successfully. We'll get back to you as soon as possible."
  let detailsRows = 6
  if (contactType === QUIZ_FEEDBACK) {
    emailRequired = false
    messageLabel = 'Details'
    header = 'Let us know what you are looking for and we’ll get back to you'
    successMsg = 'Thank you for your feedback!'
    detailsRows = 3
  }

  if (contactDetails.contectReason === CONTACT_REASON_ADDRESS_CHANGE) {
    messageLabel = 'Type your updated address here'
  }

  useEffect(async () => {
    if (!router.isReady) return
    console.log({ orderKey })
    if (orderKey) {
      const response = await getOrderConfirmationFromService({ key: orderKey })
      console.log({ response })
      if (response) {
        const order = response.order
        if (order) {
          setContactDetails({
            orderKey: order.key,
            contectReason: CONTACT_REASON_ORDER_SUPPORT,
            firstName: order.shipping_address?.first_name,
            lastName: order.shipping_address?.last_name,
            email: order.email,
            message: `contacting regarding order ${order.key}\n`,
          })
        }
      }
    }
    return
  }, [router.isReady])

  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  })

  const onChange = (newValues) => {
    setContactDetails((prev) => ({ ...prev, ...newValues }))
  }

  const onError = (error) => {
    Mixpanel.track(`contact submit success error`, {
      ...contactDetails,
      error,
    })
    const msg = error || "Couldn't find transaction data"
    console.error(msg, error)
    setDataSaveStatus({
      severity: 'error',
      message: msg,
    })
  }

  const submitData = async () => {
    if (!contactDetails.email) {
      onError('Please fill your email')
      return
    }
    Mixpanel.track('contact submit', {
      ...contactDetails,
    })
    setDataSaveStatus((prev) => ({ ...prev, loading: true }))
    try {
      const response = await submitUserContactToService({
        ...contactDetails,
        first_name: contactDetails.firstName,
        last_name: contactDetails.lastName,
        message: contactDetails.message,
        email: contactDetails.email,
        order_key: contactDetails.orderKey,
        contect_reason: contactDetails.contectReason,
        type: contactType,
        site: site?.key,
      })
      if (response) {
        Mixpanel.track('contact submit success', {
          ...contactDetails,
        })
        setDataSaveStatus((prev) => ({
          ...prev,
          severity: 'success',
          submitted: true,
          message: successMsg,
          loading: false,
        }))
      } else {
        Mixpanel.track('contact submit success error', {
          ...contactDetails,
        })
        onError("Couldn't create order, please contact our support")
      }
    } catch (err) {
      onError(
        err?.message ||
          err ||
          "Couldn't create order, please contact our support",
      )
    }
  }

  return (
    <Box>
      {dataSaveStatus?.message && dataSaveStatus?.severity !== 'success' && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={true}
          autoHideDuration={10000}
          onClose={() => setDataSaveStatus(null)}
        >
          <Alert severity={dataSaveStatus?.severity} sx={{ width: '100%' }}>
            {dataSaveStatus?.message}
          </Alert>
        </Snackbar>
      )}
      {dataSaveStatus?.message && dataSaveStatus?.severity === 'success' ? (
        <Box sx={{ padding: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            align="center"
            color="success.main"
          >
            {dataSaveStatus?.message}
          </Typography>
        </Box>
      ) : (
        <Box padding={{ xs: 3, sm: 6 }} width={'100%'} marginBottom={4}>
          {header && (
            <Grid item xs={12}>
              {' '}
              <Typography gutterBottom>{header}</Typography>
            </Grid>
          )}
          <form noValidate autoComplete="off">
            <Grid container spacing={isMd ? 4 : 2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="contact-reason-label">
                    Reason for contact
                  </InputLabel>
                  <Select
                    labelId="contact-reason-label"
                    id="contact-reason"
                    value={contactDetails.contectReason}
                    label="Reason for contact"
                    onChange={(event) =>
                      onChange({ contectReason: event.target.value })
                    }
                  >
                    <MenuItem value={CONTACT_REASON_ADDRESS_CHANGE}>
                      Change order address
                    </MenuItem>
                    <MenuItem value={CONTACT_REASON_ORDER_SUPPORT}>
                      Order support
                    </MenuItem>
                    <MenuItem value={CONTACT_REASON_GENERAL_INQUIRY}>
                      General Inquiry
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {(contactDetails.contectReason === 'Order support' ||
                contactDetails.contectReason === 'Change order address') && (
                <Grid item xs={12}>
                  <TextField
                    sx={{ height: 54 }}
                    label="Order Confirmation ID"
                    variant="outlined"
                    color="primary"
                    size="medium"
                    fullWidth
                    value={contactDetails.orderKey || ''}
                    onChange={(event) =>
                      onChange({ orderKey: event.target.value })
                    }
                  />
                </Grid>
              )}

              {showUserDetails && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    sx={{ height: 54 }}
                    label="First name"
                    variant="outlined"
                    color="primary"
                    size="medium"
                    fullWidth
                    value={contactDetails.firstName || ''}
                    onChange={(event) =>
                      onChange({ firstName: event.target.value })
                    }
                  />
                </Grid>
              )}
              {showUserDetails && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    sx={{ height: 54 }}
                    label="Last name"
                    variant="outlined"
                    color="primary"
                    size="medium"
                    fullWidth
                    value={contactDetails.lastName || ''}
                    onChange={(event) =>
                      onChange({ lastName: event.target.value })
                    }
                  />
                </Grid>
              )}

              {showUserDetails && (
                <Grid item xs={12}>
                  <TextField
                    required={emailRequired ? true : undefined}
                    sx={{ height: 54 }}
                    label="Email"
                    type="email"
                    variant="outlined"
                    color="primary"
                    size="medium"
                    fullWidth
                    value={contactDetails.email || ''}
                    onChange={(event) =>
                      onChange({ email: event.target.value })
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  required
                  label={messageLabel}
                  multiline
                  rows={detailsRows}
                  variant="outlined"
                  color="primary"
                  size="medium"
                  fullWidth
                  value={contactDetails.message || ''}
                  onChange={(event) =>
                    onChange({ message: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <LoadingButton
                  loading={dataSaveStatus?.loading}
                  sx={{ height: 54 }}
                  variant="contained"
                  color="primary"
                  size="medium"
                  fullWidth
                  onClick={submitData}
                >
                  Submit
                </LoadingButton>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography component="p" variant="body2" align="left">
                    By sending this message you agree to our{' '}
                    <Link
                      href="/privacy?ref=contact_privacy"
                      color={theme.palette.text.primary}
                      fontWeight={'700'}
                    >
                      Privacy Policy
                    </Link>
                    ,{' '}
                    <Link
                      href="/terms?ref=contact_data"
                      color={theme.palette.text.primary}
                      fontWeight={'700'}
                    >
                      Data Policy
                    </Link>{' '}
                    and{' '}
                    <Link
                      href="/terms?ref=contact_cookie"
                      color={theme.palette.text.primary}
                      fontWeight={'700'}
                    >
                      Cookie Policy
                    </Link>
                    .
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      )}
    </Box>
  )
}

Form.propTypes = {
  contactType: PropTypes.string,
}
export default Form
